import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Users, Search, Loader2, Shield, LogOut, ChevronRight, Briefcase, CalendarCheck,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  ACTIVE: 'bg-green-100 text-green-800 border-green-300',
  DEACTIVATED: 'bg-slate-200 text-slate-700 border-slate-300',
  SUSPENDED: 'bg-red-100 text-red-800 border-red-300',
};

const FILTERS = [
  ['all', 'All accounts'], ['active', 'Active'], ['inactive', 'Inactive'],
  ['suspended', 'Suspended'], ['dual', 'Dual-role'], ['client-only', 'Client-only'],
  ['has-bookings', 'Has bookings'], ['no-bookings', 'No bookings'], ['deleted', 'Deleted'],
];
const SORTS = [
  ['newest', 'Newest signup'], ['oldest', 'Oldest signup'],
  ['most-bookings', 'Most bookings'], ['fewest-bookings', 'Fewest bookings'],
  ['recent-booking', 'Most recent booking'], ['alphabetical', 'Alphabetical'],
];

function AdminNav({ navigate, active }) {
  const item = (path, label, Icon, key) => (
    <Button variant={active === key ? 'secondary' : 'ghost'} size="sm"
      onClick={() => navigate(path)} data-testid={`nav-${key}`}>
      <Icon className="h-4 w-4 mr-1.5" /> {label}
    </Button>
  );
  return (
    <div className="bg-card border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 py-2 overflow-x-auto">
        {item('/admin/clients', 'Client Accounts', Users, 'clients')}
        {item('/admin/promo-codes', 'Promo Codes', Briefcase, 'promo-codes')}
        {item('/admin/system', 'System', Shield, 'system')}
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="nav-admin-home">
          Admin Panel
        </Button>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return '—'; }
}

export default function AdminClientsPage() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('newest');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/clients`, {
        headers, params: { q: q.trim() || undefined, filter, sort, page: 1, pageSize: 200 },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load client accounts');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sort, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <div className="flex items-center gap-6">
            <Logo variant="compact" size="sm" />
            <div className="border-l pl-6 flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-lg"><Users className="h-5 w-5 text-primary" /></div>
              <h1 className="text-lg font-semibold" data-testid="admin-clients-title">Client Accounts</h1>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="mr-2 h-4 w-4" /><span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </div>

      <AdminNav navigate={navigate} active="clients" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
              placeholder="Search by client name, email, or provider business…"
              data-testid="clients-search" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="md:w-48" data-testid="clients-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FILTERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="md:w-52" data-testid="clients-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <p className="text-sm text-muted-foreground" data-testid="clients-count">{total} account{total === 1 ? '' : 's'}</p>

        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="h-7 w-7 animate-spin" /></div>
        ) : items.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="clients-empty">No client accounts match.</CardContent></Card>
        ) : (
          <div className="grid gap-2" data-testid="clients-list">
            {items.map((c) => (
              <Card key={c.id} data-testid={`client-row-${c.id}`}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/admin/clients/${c.id}`)}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate">{c.name || '(no name)'}</p>
                      <Badge className={`${STATUS_BADGE[c.accountStatus] || ''} border text-xs`} data-testid={`status-${c.id}`}>
                        {c.accountStatus}
                      </Badge>
                      {c.hasProviderWorkspace && (
                        <Badge className="bg-purple-100 text-purple-800 border-purple-300 border text-xs" data-testid={`dual-${c.id}`}>
                          Client + Provider
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{c.email}</p>
                    <p className="text-xs text-muted-foreground mt-1">Joined {fmtDate(c.signupDate)}</p>
                  </div>
                  <div className="hidden sm:flex flex-col items-end text-sm">
                    <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5 text-muted-foreground" /> {c.providerCount} provider{c.providerCount === 1 ? '' : 's'}</span>
                    <span className="flex items-center gap-1 mt-1"><CalendarCheck className="h-3.5 w-3.5 text-muted-foreground" /> {c.totalBookings} booking{c.totalBookings === 1 ? '' : 's'}</span>
                    <span className="text-xs text-muted-foreground mt-1">
                      {c.upcomingBookings} upcoming · {c.completedBookings} done · {c.cancelledBookings} cancelled
                    </span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
